import React, { useMemo } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Text } from '../../components/ui'
import type { SupplierQuote } from '../../types/supplierQuotes'
import {
  buildCompareMatrix,
  formatCurrency,
  formatQuoteNumber,
  lowestUnitValueForRow,
} from './propostasUi'
import { formatRequestNumber } from './solicitacaoUi'

type Props = {
  open: boolean
  quotes: SupplierQuote[]
  showValues?: boolean
  onClose: () => void
}

const PropostasCompareModal: React.FC<Props> = ({
  open,
  quotes,
  showValues = true,
  onClose,
}) => {
  const matrix = useMemo(() => buildCompareMatrix(quotes), [quotes])
  const quoteIds = quotes.map((q) => q.id)
  const requestNumber = quotes[0]?.request?.request_number

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
              Comparar propostas
            </Heading>
            <Text size="sm" color="muted" className="mt-1">
              {requestNumber != null
                ? `Solicitação ${formatRequestNumber(requestNumber)} · ${quotes.length} propostas`
                : `${quotes.length} propostas`}
            </Text>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4">
          {quotes.length < 2 ? (
            <Text color="muted">É necessário ter ao menos duas propostas na solicitação.</Text>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/40">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                      Item
                    </th>
                    {quotes.map((quote) => (
                      <th
                        key={quote.id}
                        className={`px-3 py-2 text-left font-medium ${
                          showValues && matrix.bestQuoteId === quote.id
                            ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                            : 'text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        <div>{formatQuoteNumber(quote.quote_number)}</div>
                        <div className="font-normal">{quote.supplier?.name || '—'}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {matrix.rows.map((row) => {
                    const bestUnit = showValues
                      ? lowestUnitValueForRow(row, quoteIds)
                      : null
                    return (
                      <tr key={row.key}>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">
                          {row.label}
                        </td>
                        {quotes.map((quote) => {
                          const cell = row.cells[quote.id]
                          if (!cell) {
                            return (
                              <td
                                key={quote.id}
                                className="px-3 py-2 text-gray-400 dark:text-gray-500"
                              >
                                —
                              </td>
                            )
                          }
                          const isBest =
                            showValues &&
                            bestUnit != null &&
                            cell.unitValue === bestUnit &&
                            quoteIds.length > 1
                          return (
                            <td
                              key={quote.id}
                              className={`px-3 py-2 ${
                                isBest
                                  ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-200'
                                  : 'text-gray-900 dark:text-white'
                              }`}
                            >
                              {showValues ? (
                                <>
                                  <div>{formatCurrency(cell.unitValue)}</div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {cell.quantity} × {formatCurrency(cell.lineTotal)}
                                  </div>
                                </>
                              ) : (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Qtd {cell.quantity}
                                </div>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                  <tr className="bg-gray-50 font-semibold dark:bg-gray-900/40">
                    <td className="px-3 py-3 text-gray-900 dark:text-white">Total</td>
                    {quotes.map((quote) => (
                      <td
                        key={quote.id}
                        className={`px-3 py-3 ${
                          showValues && matrix.bestQuoteId === quote.id
                            ? 'bg-green-100 text-green-900 dark:bg-green-900/40 dark:text-green-100'
                            : 'text-gray-900 dark:text-white'
                        }`}
                      >
                        {showValues ? formatCurrency(matrix.totals[quote.id] || 0) : '—'}
                        {showValues && matrix.bestQuoteId === quote.id && (
                          <div className="text-xs font-normal">Melhor total</div>
                        )}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-gray-200 px-5 py-4 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </div>
  )
}

export default PropostasCompareModal
